import { DefaultApiFactory } from "@clients/velys-infrastructure/api";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import config from "@/config";
import { useAuthStore } from "../stores/useAuthStore";
import { useBoundStore } from "../stores/useBoundStore";
import { silentRenew } from "../auth/authBridge";
import { isPublicRoute } from "../helpers";

const jnjInstance: AxiosInstance = axios.create();

const requestHandler = (request: InternalAxiosRequestConfig) => {
  const { user_token } = useAuthStore.getState();

  if (user_token) {
    request.headers.Authorization = `Bearer ${user_token}`;
  }

  return request;
};

const responseHandler = (response: AxiosResponse) => {
  return response;
};

const errorHandler = async (error: AxiosError) => {
  const status = error.response?.status;
  const originalRequest = error.config;

  // On 401/403, try a silent token refresh before giving up. Only log out if
  // the refresh fails (refresh token expired or rejected by Cognito).
  if ((status === 401 || status === 403) && originalRequest) {
    let newToken: string | null = null;
    try {
      newToken = await silentRenew();
    } catch {
      console.log("no refresh");
    }

    if (newToken) {
      useAuthStore.getState().setUserToken(newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      // Replay through the bare axios instance (no interceptors) so a repeat
      // 401 can't loop back into this handler.
      return axios(originalRequest);
    }

    // Public routes (e.g. /legal) are viewable while signed out. A stray 401
    // from a rehydrated stale token must not force-logout / clear the cookie
    // here — only bounce when the user is on a route that requires auth.
    if (!isPublicRoute()) {
      const { signOutRedirect } = useBoundStore.getState();
      signOutRedirect();
    }
  }

  return Promise.reject(error);
};

jnjInstance.interceptors.request.use((request) => requestHandler(request));
jnjInstance.interceptors.response.use(responseHandler, errorHandler);

const velysInstance = DefaultApiFactory(
  // @ts-expect-error - ignore
  {},
  config.apiBaseUrl,
  jnjInstance,
);

export { velysInstance };
